import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useTimeout } from '../../../hooks/useTimeout';
import { Loading } from '../../UI';

interface InputDropdownProps {
  label: string;
  selected: string;
  placeholder: string;
  onChange: (searchTerm: string) => void;
  loading: boolean;
}

/**
 * onChange - called when input value changes
 * children - search results array
 * selected - changes input value by specified
 */
export const InputDropdown: React.FC<InputDropdownProps> = ({
  children,
  label,
  placeholder,
  onChange,
  loading,
  selected,
}) => {
  // INPUT
  const [isLabelVisible, setIsLabelVisible] = useState(false);
  const { value, setValue } = useTimeout(() => {
    onChange(value || '');
  }, 1000);

  // DROPDOWN
  const [referenceEl, setReferenceEl] = useState<HTMLDivElement | null>(null);
  const [resultsEl, setResultsEl] = useState<HTMLDivElement | null>(null);
  const [dropdownTouched, setDropdownTouched] = useState(false);

  const popper = usePopper(referenceEl, resultsEl, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const inputDropdownRef = useRef<HTMLDivElement | null>(null);

  function changeResultsVisibility(v: boolean) {
    setIsDropdownVisible(v);
    setDropdownTouched(false);
    popper.update?.();
  }

  useEffect(() => {
    setValue(selected);
    changeResultsVisibility(false);
  }, [selected]);

  useEffect(() => {
    function handleClose(e: MouseEvent) {
      if (!dropdownTouched) return;
      if (!resultsEl?.contains(e.target as Node)) {
        changeResultsVisibility(false);
      }
    }

    document.addEventListener('click', handleClose);
    return () => {
      document.removeEventListener('click', handleClose);
    };
  }, [dropdownTouched, resultsEl]);

  return (
    <div className="w-full" ref={inputDropdownRef}>
      <div className="flex flex-col relative" ref={setReferenceEl}>
        {(isLabelVisible || selected) && (
          <label className="absolute -top-4 left-0 mb-2 text-xs font-medium text-black dark:text-white">
            {label}
          </label>
        )}
        <input
          type="text"
          placeholder={isLabelVisible ? '' : placeholder}
          value={value || ''}
          className="border-b border-black py-2 dark:bg-grey-90 dark:text-grey-50 dark:border-white"
          onFocus={() => {
            setIsLabelVisible(true);
            changeResultsVisibility(true);
          }}
          onBlur={() => {
            setIsLabelVisible(false);
          }}
          onChange={(event) => setValue(event.target.value)}
        />
        {!loading && (
          <span className="absolute bottom-2.5 -right-0.5 material-icons text-black dark:text-white text-lg pointer-events-none">
            search
          </span>
        )}
        <Loading
          size={'small'}
          status={loading}
          className="absolute bottom-2.5 -right-0.5 material-icons dark:text-white text-black text-lg pointer-events-none"
        />
      </div>
      <div
        ref={setResultsEl}
        style={{
          ...popper.styles.popper,
          minWidth: inputDropdownRef.current?.scrollWidth,
          maxWidth: inputDropdownRef.current?.scrollWidth,
        }}
        {...popper.attributes.popper}
        className={isDropdownVisible ? 'block' : 'hidden'}
        onMouseEnter={() => setDropdownTouched(true)}
      >
        <div
          className={`bg-white ${
            !isEmpty(children) && 'border border-grey-30'
          } rounded-xl shadow-light max-h-56 overflow-y-auto dark:bg-grey-90
            dark:border-grey-70`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
